import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import AddBoxIcon from "@material-ui/icons/AddBox";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClassParams,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import CustomLoadingOverlay from "../../../../../../components/loaders/LazyLoading/LazyLoading";
import { useSearchParts } from "./hooks/useSearchParts";
import { useSearchTemplateParts } from "./hooks/useSearchTemplateParts";
import { AssetAddPartsDialog } from "../asset-resources-dialogs/parts-resource/asset-add-parts-dialog";
import { PartPicker } from "./components/linker-parts/parts-picker";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import { useTemplatePartsActionsAndButtons } from "./hooks/useTemplatePartsGridActionsAndButtons";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		btnAdd: {
			position: "absolute",
			padding: 0,
			bottom: 5.5,
			right: 13,
		},
		btnCached: {
			position: "absolute",
			padding: 0,
			bottom: 8,
			left: 10,
		},
	}),
);

const VITE_DOCUMENT_PATH =
	import.meta.env.VITE_DOCUMENT_PATH ||
	"https://mill-cmms.roanokesteel.com/vaultix/";

type Props = {
	TemplateId: number | null;
	newMode?: boolean;
	AssetId: number | null;
	searchAssetResources: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShowAssetPartsPicker: Function;
	isLoadingResourcesList?: boolean;
	TemplateName: string;
	AssetName: string;
};

export const TemplatePartsTable = ({
	TemplateId,
	newMode,
	AssetId,
	searchAssetResources,
	show,
	setShowAssetPartsPicker,
	isLoadingResourcesList,
	TemplateName,
	AssetName,
}: Props) => {
	const classes = useStyles();
	const [openViewer, setOpenViewer] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [errorApi, setErrorApi] = useState("");

	const [isTemplatePartsLoading, setIsTemplatePartsLoading] =
		useState<boolean>(false);
	const [templatePartsError, setTemplatePartsError] = useState<string>("");

	const [linkPartError, setLinkPartError] = useState<string>("");
	const [removePartError, setRemovePartError] = useState<string>("");

	const [showLinkResourcesToAssetDialog, setShowLinkResourcesToAssetDialog] =
		useState<boolean>(false);

	const [localShowInheritMessage, setLocalShowInheritMessage] = useState(false);

	const [listMessage, setListMessage] = useState<string>("");

	const [openPartsPicker, setOpenPartsPicker] = useState(false);

	const {
		state: { assetParts, templatePartsList, partSearch, rowSelectedInPart },
		actions: {
			setShowListOfParts,
			setPartSearch,
			setRowSelectedInPart,
			setLoaderListSearchParts,
			setTemplatePartsList,
			setPartIdRowSelected,
		},
	} = useTemplatesContext();

	const debouncedSearch = useRef(
		debounce((partSearch?: string) => {
			searchParts().then(() => {
				setLoaderListSearchParts(false);
			});
		}, 1000),
	).current;

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listPartSelected, setListPartSelected] = useState<any[]>([]);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const searchTemplateParts = useSearchTemplateParts({
		TemplateId,
		setIsTemplatePartsLoading,
		setTemplatePartsError,
	});

	// SEARCH PARTS
	const searchParts = useSearchParts({ setIsLoading, setError });

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const getRowStyle = useCallback(
		(params: RowClassParams): { [key: string]: string } | undefined => {
			if (params.data.IsAssetResource) {
				return { backgroundColor: "#e8f5e9" };
			}
		},
		[],
	);

	const {
		refresh,
		addRow,
		removeParts,
		addButton,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	} = useTemplatePartsActionsAndButtons({
		TemplateId,
		newMode,
		gridRef: gridApi,
		searchTemplateParts,
		setRemovePartError,
		setListMessage,
	});

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 33,
			maxWidth: 33,
			filter: false,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
		});
		newColumns.push({
			field: "PartName",
			headerName: "Part",
			flex: 2,
			minWidth: 170,
			maxWidth: 170,
		});
		newColumns.push({
			type: "columnProperties",
			field: "Description",
			headerName: "Description",
			flex: 4,
			minWidth: 290,
		});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid
							item
							xs={12}
							md={12}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid item xs={12} md={12}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			setRowSelectedInPart(params.node?.data);
			const rowSelectedInPartInTable = params.node?.data;
			const selectedParts = gridApi?.getSelectedNodes();

			const result: (DefaultMenuItem | MenuItemDef)[] = [];
			if (!saveButton) {
				result.push({
					name: "Add Part Link",
					action: () => setOpenPartsPicker(true),
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (selectedParts?.length) {
				result.push({
					name: "Remove Part Link",
					action: () => {
						setRowSelectedInPart(params.node?.data);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (
				!selectedParts?.length &&
				rowSelectedInPartInTable?.id !== -1 &&
				rowSelectedInPartInTable
			) {
				result.push({
					name: "Remove Part Link",
					action: () => {
						setRowSelectedInPart(params.node?.data);
						setListPartSelected([]);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (rowSelectedInPartInTable) {
				result.push("separator");
			}

			result.push({
				name: "Refresh",
				action: () => refresh(),
				icon: '<i class="fas fa-sync-alt"></i>',
			});

			return result;
		},
		[saveButton, addRow, TemplateId, gridApi, rowSelectedInPart],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const rowCount = event.api.getSelectedNodes().length;
		const listPartsSelected = event.api.getSelectedNodes();
		setListPartSelected(listPartsSelected);
		rowCount > 0 ? setDeleteButton(true) : setDeleteButton(false);
	}, []);

	const openModalViewerActive = () => {
		const selectedRow: RowNode<any>[] | any = gridApi?.getSelectedNodes();
		if (selectedRow[0]!.data !== undefined) {
			setRowSelectedInPart(selectedRow[0]!.data);
			setOpenViewer(!openViewer);
		}
	};

	useEffect(() => {
		if (partSearch.length >= 3) {
			setLoaderListSearchParts(true);
			setShowListOfParts(true);
			debouncedSearch(partSearch);
		} else {
			setShowListOfParts(false);
		}
	}, [partSearch]);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	useEffect(() => {
		setSaveButton(false);
		setDiscardButton(false);
		setAddButton(true);
		setDeleteButton(false);
		setDeleteOpen(false);
		setPartSearch("");
	}, [TemplateId]);

	useEffect(() => {
		renderColumns();
	}, []);

	useEffect(() => {
		const hasAssetResource = templatePartsList.some(
			(part) => part.IsAssetResource === true,
		);
		const shouldShowInheritMessage =
			(!hasAssetResource || templatePartsList.length === 0) &&
			assetParts.length > 0;

		if (show && newMode && shouldShowInheritMessage) {
			setLocalShowInheritMessage(true);
		} else if (!show) {
			setLocalShowInheritMessage(false);
		}
	}, [assetParts]);

	return (
		<>
			{show && (
				<div style={containerStyle}>
					<Grid container style={gridStyle}>
						<Grid
							item
							md={12}
							xs={12}
							style={{ paddingBottom: 45 }}
							className={classes.agGridContainer}
						>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={templatePartsList}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={openModalViewerActive}
								getRowStyle={getRowStyle}
								overlayNoRowsTemplate={"No parts added"}
								loading={isTemplatePartsLoading}
							></AgGridReact>
							{addButton && (
								<>
									<IconButton
										onClick={() => setOpenPartsPicker(true)}
										aria-label="add"
										color="primary"
										className={classes.btnAdd}
									>
										<AddBoxIcon style={{ width: 35, height: 35 }} />
									</IconButton>
								</>
							)}
							<IconButton
								onClick={refresh}
								aria-label="delete"
								className={classes.btnCached}
							>
								<AutorenewIcon
									style={{ width: 30, height: 30, color: "green" }}
								/>
							</IconButton>
						</Grid>
						<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
							<DialogTitle id="responsive-dialog-title">
								{"WARNING"}
							</DialogTitle>
							<DialogContent>
								<DialogContentText style={{ padding: 0, margin: 0 }}>
									{listPartSelected?.length === 1 ? (
										<p style={{ margin: "0" }}>
											REMOVE <b>{listPartSelected[0].data.PartName}</b> FROM
											WORK ORDER <b>{listPartSelected[0].data.TemplateName}?</b>
										</p>
									) : listPartSelected.length > 1 ? (
										<>
											<p style={{ margin: 0 }}> REMOVE:</p>
											{listPartSelected?.map((part) => (
												<p key={part.data.PartName} style={{ margin: "5px 0" }}>
													<b>⁃ {part.data.PartName}, </b>
												</p>
											))}
											from Template?
										</>
									) : rowSelectedInPart?.PartName ? (
										<p style={{ margin: "0" }}>
											REMOVE <b>{rowSelectedInPart?.PartName}</b> from template
											order?
										</p>
									) : null}
								</DialogContentText>
							</DialogContent>
							<DialogActions style={{ padding: "16px 24px" }}>
								<Button
									onClick={() => setDeleteOpen(false)}
									autoFocus
									color="default"
								>
									CANCEL
								</Button>
								<Button
									autoFocus
									color="secondary"
									onClick={() => {
										if (listPartSelected.length > 0) {
											removeParts(listPartSelected);
										} else if (rowSelectedInPart) {
											removeParts([rowSelectedInPart]);
										}
									}}
								>
									REMOVE
								</Button>
							</DialogActions>
						</Dialog>
						<AssetAddPartsDialog
							show={showLinkResourcesToAssetDialog}
							onHide={(shouldUpdate: boolean) => {
								setShowLinkResourcesToAssetDialog(false);
								if (shouldUpdate) {
									searchTemplateParts();
								}
							}}
							AssetId={AssetId as number}
							AssetName={AssetName as string}
							TemplateName={TemplateName as string}
							searchAssetResources={searchAssetResources}
						/>
						<PartPicker
							searchParts={searchParts}
							show={openPartsPicker}
							onHide={(shouldShow: boolean) => {
								if (shouldShow) {
									searchTemplateParts();
								}
								setOpenPartsPicker(false);
							}}
							TemplateId={TemplateId as number}
							isLoading={isLoading}
							setShowLinkResourcesToAssetDialog={
								setShowLinkResourcesToAssetDialog
							}
						/>
					</Grid>
					<ErrorModal error={error} onHide={() => setError("")} />
					<ErrorModal error={errorApi} onHide={() => setErrorApi("")} />
					<ErrorModal
						error={templatePartsError}
						onHide={() => setTemplatePartsError("")}
					/>
					<ErrorModal
						error={linkPartError}
						onHide={() => setLinkPartError("")}
					/>
					<ErrorModal
						error={removePartError}
						onHide={() => setRemovePartError("")}
					/>
					<ErrorModal error={listMessage} onHide={() => setListMessage("")} />
				</div>
			)}
		</>
	);
};
